import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API, apiClient } from "../../api/config/axiosConfig";
import endpoints from "../../api/endpoints/endpoints";
import {
  getUser,
  setSession,
  setUser as setStoredUser,
  clearSession,
} from "../../auth/tokenStorage";

// Auth thunks use `apiClient` (no refresh interceptor) for the calls that
// establish or rotate the session — they must not recurse through the
// 401 → refresh flow that lives on `API`. Logout goes through `API` so it
// picks up the CSRF interceptor.
const rejectFromAxios = (error, fallback) => {
  if (error.response) {
    return error.response.data?.message || fallback;
  }
  return "Network error. Please check your connection.";
};

// Login. Backend sets HttpOnly access/refresh cookies + the JS-readable
// `csrf_auth` cookie; the response body carries only the user blob.
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(endpoints.login, {
        email,
        password,
      });

      const userData = data.details?.user || data.user || data.data;
      if (!userData) {
        return rejectWithValue("Invalid response format from server");
      }

      setSession({ user: userData });
      return { user: userData };
    } catch (error) {
      console.error("Login error:", error);
      return rejectWithValue(rejectFromAxios(error, "Login failed"));
    }
  }
);

// Register does NOT log the user in — they have to verify the email OTP
// next. Backend returns success + the registered user blob, no cookies.
export const signupUser = createAsyncThunk(
  "auth/signupUser",
  async ({ email, password, full_name, role }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(endpoints.signup, {
        email,
        full_name,
        role,
        password,
      });
      if (!data.success) {
        return rejectWithValue(data.message || "Registration failed");
      }

      return { user: data.details, message: data.message };
    } catch (error) {
      console.error("Signup error:", error);
      return rejectWithValue(rejectFromAxios(error, "Registration failed"));
    }
  }
);

// Admin login step 1 — sends OTP, no session change.
export const adminLogin = createAsyncThunk(
  "auth/adminLogin",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post("/api/users/admin-login/", {
        email,
        password,
      });

      return {
        message: data.message || "Access code sent to your email",
      };
    } catch (error) {
      console.error("Admin login error:", error);
      return rejectWithValue(rejectFromAxios(error, "Admin login failed"));
    }
  }
);

// Admin login step 2 — verifies OTP. Backend sets cookies on success.
export const adminLoginVerify = createAsyncThunk(
  "auth/adminLoginVerify",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(
        "/api/users/admin-login/verify/",
        { email, otp }
      );

      const userData = data.details?.user || data.user || data.data;
      if (!userData) {
        return rejectWithValue("Invalid response format from server");
      }

      setSession({ user: userData });
      return {
        user: userData,
        message: data.message || "Admin login successful",
      };
    } catch (error) {
      console.error("Admin OTP verification error:", error);
      return rejectWithValue(rejectFromAxios(error, "Invalid access code"));
    }
  }
);

// Server clears the HttpOnly cookies; we drop the local user blob.
// Use `API` so CSRF + cookies are attached. We swallow network errors
// because the local state must still reflect "logged out" either way.
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue: _r }) => {
    try {
      await API.post(endpoints.logout);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearSession();
    }
    return true;
  }
);

// On app boot, restore from the cached user blob. If access has expired
// but refresh is still valid, the first authenticated API call will
// trigger the silent refresh interceptor in `axiosConfig`.
export const checkAuthStatus = createAsyncThunk(
  "auth/checkAuthStatus",
  async (_, { rejectWithValue }) => {
    try {
      const userData = getUser();
      if (userData) {
        return { user: userData };
      }
      return rejectWithValue("No authentication data found");
    } catch (error) {
      console.error("Auth check error:", error);
      clearSession();
      return rejectWithValue("Invalid authentication data");
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(
        "/api/users/forgot-password/",
        { email }
      );

      return data.message || "Password reset email sent successfully";
    } catch (error) {
      console.error("Forgot password error:", error);
      return rejectWithValue(
        rejectFromAxios(error, "Failed to send reset email")
      );
    }
  }
);

// Refresh sends no body; the refresh token is in the HttpOnly cookie and
// the backend rotates and re-sets all three cookies on the response.
export const refreshAccessToken = createAsyncThunk(
  "auth/refreshAccessToken",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await apiClient.post(endpoints.refreshToken, null);

      if (!data.success) {
        return rejectWithValue(data.message || "Failed to refresh token");
      }

      return true;
    } catch (error) {
      console.error("Token refresh error:", error);
      return rejectWithValue(
        rejectFromAxios(error, "Failed to refresh token")
      );
    }
  }
);


const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  message: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      clearSession();
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.message = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    updateUser: (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload,
      };
      setStoredUser(state.user);
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        state.message = "Login successful";
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
      })
      // Refresh token cases — no auth state change on success (cookies
      // were rotated server-side); on failure we drop the user.
      .addCase(refreshAccessToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshAccessToken.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.isAuthenticated = true;
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        clearSession();
      })
      // Signup cases — registration does NOT authenticate the user.
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.message = action.payload.message || "Registration successful";
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Admin login step 1 cases
      .addCase(adminLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.message = action.payload.message;
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Admin login step 2 cases
      .addCase(adminLoginVerify.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(adminLoginVerify.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        state.message = action.payload.message;
      })
      .addCase(adminLoginVerify.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
      })
      // Logout cases — same effect on success or failure: locally logged out.
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        state.message = "Logged out successfully";
      })
      .addCase(logoutUser.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        state.message = "Logged out successfully";
      })
      // Check auth status cases
      .addCase(checkAuthStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
      })
      .addCase(checkAuthStatus.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
      })
      // Forgot password cases
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  logout,
  clearError,
  clearMessage,
  setLoading,
  updateUser,
} =
  authSlice.actions;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsSeller = (state) => state.auth.user?.role === "seller";
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectAuthMessage = (state) => state.auth.message;

export default authSlice.reducer;
